import { useEffect, useMemo, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { t } from "@lingui/macro";
import { Box } from "@otrium/core";
import { locale } from "src/config/app-config";
import { useAppActions } from "src/hooks/stores/use-app-store";
import { googleTagManager } from "src/tracking/googleTagManager";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  PopularCategory,
  HomepagePopularCategoryData,
  ProductLabelFilter,
  ImageBannerData,
  Homepage,
  SeoQueryResponse,
} from "src/types/graphql.d";
import { getI18nRoute } from "src/utils/i18nRoutes";
import {
  useHomepageRecommendations,
  useNewInHomepage,
  useShopsHomepage,
} from "src/hooks/useHomepage";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import Error from "src/pages/_error";
import classNames from "classnames";
import layout from "src/styles/layout.module.scss";
import { PageHead } from "src/molecules/PageHead";
import { PromotionBanner } from "src/molecules/PromotionBanner";
import { BrandCarousel } from "src/molecules/BrandCarousel";
import { ProductCarousel } from "src/molecules/ProductCarousel";
import { CategoryCarousel } from "src/molecules/CategoryCarousel";
import { CuratedCategory } from "src/molecules/CuratedCategory";
import { PromoBlock } from "src/molecules/PromoBlock";
import { RetroRevival } from "src/molecules/RetroRevival";
import { theme } from "src/theme";
import { SEGMENT_POSITIONS, SEGMENT_PROMOTION_TYPE } from "src/segment";
import { BrandsSection } from "src/organisms/BrandsSection";
import { UspValueBlock } from "src/organisms/UspValueBlock";

import { useLingui } from "@lingui/react";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { usePageViewedContext } from "src/hooks/context/use-page-viewed-context";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { useOrganizationSchema } from "src/hooks/useOrganizationSchema";
import {
  CAROUSEL_CONTENT_TYPE,
  PRODUCT_CAROUSEL_SLIDES,
  PRODUCT_CAROUSEL_SLIDES_WITH_INFO_TEXT,
  SUPPORTED_KEYS,
} from "src/utils/constants";
import type { SupportedKeys } from "src/types/Carousels";

const GeoRedirectCard = dynamic(
  () => import("src/molecules/GeoRedirectCard").then((m) => m.GeoRedirectCard),
  { ssr: false }
);

// Sections are ordered by their CMS position, so the USP block needs an order
// beyond any of them to stay last in the page shell's flex column.
const LAST_SECTION_ORDER = 999;

const HOMEPAGE_RECOMMENDATIONS_CAROUSEL_ID = "homepage_recommendations";

type HomeViewProps = {
  homepage: Homepage;
  seoContent?: SeoQueryResponse | null;
};

function HomeView({ homepage, seoContent }: HomeViewProps): React.ReactElement {
  const { i18n } = useLingui();
  const router = useRouter();
  const { data: session } = useSession();
  const { setHomepageMounted, setPageViewedProperties } = useAppActions();
  const localeGenderId = useMemo(
    () => localeFix({ gender: router.query.slug as string }),
    [router.query.slug]
  );
  const { currentShopType } = useShopTypeContext();
  const isLoggedIn = useIsLoggedIn();
  const { pageViewTracked } = usePageViewedContext();
  const pageMountedRef = useRef(false);

  const genderId = router.query.slug as string;

  const { enableGatedHomepage } = useFeatureFlags();

  useEffect(() => {
    if (pageMountedRef.current) {
      return;
    }

    setHomepageMounted(true);
    setPageViewedProperties({
      page_type: "home",
    });

    pageMountedRef.current = true;
  }, [localeGenderId, setHomepageMounted, setPageViewedProperties]);

  useEffect(() => {
    if (session?.user.email && pageViewTracked) {
      googleTagManager.trackHomePage(session?.user.email);
    }
  }, [session?.user.email, pageViewTracked]);

  const { data: newInData, refetch: refetchNewIn } = useNewInHomepage({
    gender: localeGenderId,
    limit: 15,
    labels: [ProductLabelFilter.NewIn],
  });

  const { data: shopsData, refetch: refetchShops } = useShopsHomepage({
    gender: localeGenderId,
  });

  const recommendations = useHomepageRecommendations({
    gender: localeGenderId,
    limit: 15,
  });

  useEffect(() => {
    if (localeGenderId !== currentShopType) {
      try {
        void refetchNewIn();
        void refetchShops();
      } catch (e) {
        console.error(e);
      }
    }
  }, [refetchNewIn, refetchShops, localeGenderId, currentShopType]);

  const {
    promotionBannerSection,
    hasPromotionBanner,
    promotionBanner,
    shops,
    department,
    categories,
    actionBlock,
    inspirationalSection,
    newIn,
    popularCategories,
    marketingBlocks,
  } = useMemo(() => {
    const promotionBannerSection = homepage?.PromotionBanners;
    const hasPromotionBanner =
      promotionBannerSection?.data?.length &&
      promotionBannerSection?.data?.length >= 1;
    const promotionBanner = promotionBannerSection?.data?.[0];

    return {
      promotionBannerSection,
      hasPromotionBanner,
      promotionBanner,
      shops: shopsData?.homepage?.Shops || homepage?.Shops,
      department: homepage?.Departments?.[0],
      categories: homepage?.Categories,
      actionBlock: homepage?.ActionBlocks?.[0],
      inspirationalSection: homepage?.InspirationalSections?.[0],
      newIn: newInData?.homepage?.productBlocks?.[0],
      popularCategories: homepage?.PopularCategories?.[0],
      marketingBlocks: homepage?.MarketingBlocks?.[0],
    };
  }, [homepage, newInData, shopsData]);

  const showSection = useCallback(
    (gated: boolean) => isLoggedIn || (enableGatedHomepage ? !gated : true),
    [isLoggedIn, enableGatedHomepage]
  );

  const description = t(
    i18n
  )`Explore all the fashion for ${genderId} at Otrium. Shop high-quality clothing, shoes, and accessories from well-known brands with discounts up to 70%!`;

  useOrganizationSchema(description);

  const brandsSectionPosition = useMemo(() => {
    const orders = [
      ...(shops ?? [])
        .filter((shop) => {
          if (shop.type === "featured_shops" && shop.data.length > 0) {
            return true;
          }

          return (
            shop.data.length >= 3 &&
            SUPPORTED_KEYS.includes(shop.type as SupportedKeys)
          );
        })
        .map((shop) => shop.position),
    ];

    if (department) {
      orders.push(department.position);
    }

    return Math.min(...orders);
  }, [shops, department]);

  // The recommendations carousel shares New In's CSS order and comes after it in the JSX, so it
  // sits directly below New In; without New In it sits below the brands section.
  const fallbackRecommendationsOrder = isFinite(brandsSectionPosition)
    ? brandsSectionPosition
    : 0;
  const recommendationsOrder =
    newIn && newIn.position >= 0
      ? newIn.position
      : fallbackRecommendationsOrder;
  // Half a step after its order, so it ranks after the carousel it shares that order with.
  const recommendationsTrackingKey = recommendationsOrder + 0.5;

  const marketingBlocksData = useMemo(() => {
    const marketingData = marketingBlocks?.data || [];

    return marketingData
      .filter(
        ({ image_banners, position }) => image_banners?.length && position !== 0
      )
      .sort((a, b) => a.position - b.position)
      .flatMap(({ image_banners, position }) =>
        (image_banners as ImageBannerData[])
          .filter((item) => showSection(item?.gated ?? false))
          .map((banner) => ({ ...banner, position }))
      );
  }, [marketingBlocks?.data, showSection]);

  const categoryCarouselData = useMemo(
    () =>
      popularCategories?.data.filter((i) => Boolean(i.thumbnail)) as
        | PopularCategory[]
        | HomepagePopularCategoryData[],
    [popularCategories?.data]
  );

  const showInspirationalSection = useMemo(
    () =>
      inspirationalSection &&
      inspirationalSection.data?.length &&
      showSection(inspirationalSection.data.every(({ gated }) => gated)),
    [inspirationalSection, showSection]
  );

  const productCarousels = useMemo(() => {
    const positions: number[] = [];

    if (newIn && newIn.position >= 0) {
      positions.push(newIn.position);
    }

    if (categories && categories.length) {
      positions.push(...categories.map((category) => category.position));
    }

    if (recommendations) {
      positions.push(recommendationsTrackingKey);
    }

    return Object.fromEntries<number>(
      positions.sort((a, b) => a - b).map((pos, index) => [pos, index + 1])
    );
  }, [categories, newIn, recommendations, recommendationsTrackingKey]);

  if (!homepage) {
    return <Error />;
  }

  return (
    <>
      <PageHead
        title={t(i18n)`Otrium | Online Designer Outlet`}
        deeplink={`otrium://${localeGenderId}`}
        description={description}
        enableIndexation
        seoContent={seoContent}
      />
      {!!hasPromotionBanner &&
      promotionBannerSection &&
      promotionBanner &&
      showSection(!!promotionBanner?.gated) ? (
        <Box
          pb={3}
          order={promotionBannerSection.position}
          as="section"
          className={layout.containerGrid}
        >
          <PromotionBanner
            promotionBanner={promotionBanner}
            bannerPosition={promotionBannerSection.position}
            priority
          />
        </Box>
      ) : null}

      {marketingBlocks && marketingBlocksData.length > 2 ? (
        <Box
          key={`marketing-blocks-${marketingBlocks.position}`}
          pb={4}
          order={marketingBlocks.position}
          className={classNames(layout.content, layout.bleedMobile)}
        >
          <BrandCarousel
            genderId={localeGenderId}
            brands={marketingBlocksData}
            slidesDesktop={3}
            slidesTablet={3}
            slidesMobile={1.5}
            orderByNew={true}
            disableTranslation={true}
            hasVerticalPadding={false}
            promotionType={SEGMENT_PROMOTION_TYPE.imageBanners}
            homepagePosition={marketingBlocks.position}
            pageName="homepage"
            carouselId={marketingBlocks.type}
            carouselPosition={1}
          />
        </Box>
      ) : null}

      {shops ? (
        <BrandsSection
          data-testid="homepage_brand_section"
          key={localeGenderId} // key added intentionally. Forces remounting on the component on gender change
          order={isFinite(brandsSectionPosition) ? brandsSectionPosition : 0}
          shops={shops}
          department={department}
          genderId={localeGenderId}
          shopType={currentShopType}
        />
      ) : null}

      {categories
        ? categories.map((category) => {
            if (category.data.banner_image) {
              return (
                <Box
                  key={category.position}
                  py={5}
                  order={category.position}
                  as="section"
                  className={classNames(
                    layout.content,
                    layout.bleedMobile,
                    layout.mobileGap
                  )}
                >
                  <CuratedCategory
                    genderId={genderId}
                    category={category.data}
                    slidesDesktop={PRODUCT_CAROUSEL_SLIDES.desktop}
                    slidesTablet={PRODUCT_CAROUSEL_SLIDES.tablet}
                    slidesMobile={PRODUCT_CAROUSEL_SLIDES.mobile}
                    trackingData={{
                      shopType: localeGenderId,
                      pageName: "homepage",
                      carouselType: category.data.title,
                    }}
                    carouselId={category.type}
                    carouselPosition={productCarousels[category.position]}
                  />
                </Box>
              );
            }

            if (category.data.products) {
              return (
                <Box
                  key={category.position}
                  py={5}
                  order={category.position}
                  as="section"
                  className={classNames(
                    layout.content,
                    layout.bleedMobile,
                    layout.mobileGap
                  )}
                >
                  <ProductCarousel
                    title={category.data.title}
                    subTitle={category.data.subtitle}
                    products={category.data.products}
                    headerLinkHref={
                      category.data.url
                        ? category.data.url
                        : `/collections/${genderId}/${category.data.slug}`
                    }
                    slidesDesktop={PRODUCT_CAROUSEL_SLIDES.desktop}
                    slidesTablet={PRODUCT_CAROUSEL_SLIDES.tablet}
                    slidesMobile={PRODUCT_CAROUSEL_SLIDES.mobile}
                    marginSizeMobile="l"
                    genderId={localeGenderId}
                    trackingData={{
                      shopType: localeGenderId,
                      pageName: "homepage",
                      carouselType: category.data.title,
                    }}
                    carouselTrackingData={{
                      carouselId: category.type,
                      carouselPosition: productCarousels[category.position],
                      contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
                      shopType: currentShopType,
                    }}
                  />
                </Box>
              );
            }

            return null;
          })
        : null}

      {newIn?.data?.is_enabled && newIn?.data?.products?.length ? (
        <Box
          data-testid="homepage_new_in_section"
          key={newIn.position}
          order={newIn.position}
          bg="tone.white"
          pt={["space24", "space24", "space32", "space64"]}
          pb={["space48", "space48", 40, 56]}
          as="section"
          className={layout.containerGrid}
        >
          <div className={classNames(layout.bleedMobile, layout.mobileGap)}>
            <ProductCarousel
              title={t(i18n)`New In`}
              subTitle={
                newIn?.data?.personalised
                  ? session?.user.name
                    ? t(i18n)`The latest styles for ${session?.user.name}`
                    : t(i18n)`The latest styles for you`
                  : t(i18n)`The latest (and greatest) styles`
              }
              products={newIn.data.products}
              headerLinkHref={newIn.data.url}
              slidesDesktop={PRODUCT_CAROUSEL_SLIDES_WITH_INFO_TEXT.desktop}
              slidesTablet={PRODUCT_CAROUSEL_SLIDES_WITH_INFO_TEXT.tablet}
              slidesMobile={PRODUCT_CAROUSEL_SLIDES_WITH_INFO_TEXT.mobile}
              marginSizeMobile="l"
              showInfoText
              overlayBgColor={theme.colors.tone.sand}
              genderId={localeGenderId}
              responseSourceType={newIn.data.response_source_type}
              cnstrcPodId={newIn.data.pod_id ?? undefined}
              cnstrcResultId={newIn.data.result_id ?? undefined}
              cnstrcStrategyId={newIn.data.strategy_id ?? undefined}
              trackingData={{
                shopType: localeGenderId,
                pageName: "homepage",
                carouselType: "New in",
              }}
              carouselTrackingData={{
                carouselId: newIn.type,
                carouselPosition: productCarousels[newIn?.position],
                contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
                shopType: currentShopType,
              }}
            />
          </div>
        </Box>
      ) : null}

      {recommendations ? (
        <Box
          data-testid="homepage_recommendations_section"
          order={recommendationsOrder}
          bg="tone.white"
          pb={["space48", "space48", 40, 56]}
          as="section"
          className={layout.containerGrid}
        >
          <div className={classNames(layout.bleedMobile, layout.mobileGap)}>
            <ProductCarousel
              title={recommendations.title}
              products={recommendations.products}
              hideViewAll
              slidesDesktop={PRODUCT_CAROUSEL_SLIDES.desktop}
              slidesTablet={PRODUCT_CAROUSEL_SLIDES.tablet}
              slidesMobile={PRODUCT_CAROUSEL_SLIDES.mobile}
              marginSizeMobile="l"
              genderId={localeGenderId}
              cnstrcPodId={recommendations.podId}
              cnstrcResultId={recommendations.resultId}
              cnstrcStrategyId={recommendations.strategyId}
              trackingData={{
                shopType: localeGenderId,
                pageName: "homepage",
                carouselType: "Recommendations",
              }}
              carouselTrackingData={{
                carouselId: HOMEPAGE_RECOMMENDATIONS_CAROUSEL_ID,
                carouselPosition: productCarousels[recommendationsTrackingKey],
                contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
                shopType: currentShopType,
              }}
            />
          </div>
        </Box>
      ) : null}

      {actionBlock && !inspirationalSection ? (
        <Box
          py={5}
          order={actionBlock.position}
          backgroundColor="tone.sand"
          as="section"
          className={layout.containerGrid}
        >
          {actionBlock.data.code ? (
            <PromoBlock
              title={actionBlock.data.subtitle}
              subtitle={actionBlock.data.title}
              description={actionBlock.data.description}
              code={actionBlock.data.code}
              image={actionBlock.data.image}
              position={SEGMENT_POSITIONS.actionBlock}
              promotionType={SEGMENT_PROMOTION_TYPE.actionBlock}
              gated={!!(!isLoggedIn && enableGatedHomepage)}
            />
          ) : (
            <PromoBlock
              title={actionBlock.data.subtitle}
              subtitle={actionBlock.data.title}
              description={actionBlock.data.description}
              link={actionBlock.data.button_link}
              buttonText={actionBlock.data.button_title}
              image={actionBlock.data.image}
              position={SEGMENT_POSITIONS.actionBlock}
              promotionType={SEGMENT_PROMOTION_TYPE.actionBlock}
              gated={!!(!isLoggedIn && enableGatedHomepage)}
            />
          )}
        </Box>
      ) : null}

      {popularCategories && showSection(!!popularCategories?.gated) ? (
        <Box
          pb={["space16", "space16", "space32", "space32"]}
          order={popularCategories.position}
          backgroundColor="tone.white"
          as="section"
          className={layout.containerGrid}
        >
          <CategoryCarousel
            genderId={localeGenderId}
            title={t(i18n)`Popular categories`}
            categories={categoryCarouselData}
            color="tone.white"
            headerLinkHref={`${getI18nRoute({
              route: `/${localeGenderId}`,
              locale,
            })}/categories`}
            carouselPosition={1}
            slidesDesktop={categoryCarouselData.length}
            slidesTablet={categoryCarouselData.length}
            slidesMobile={2.5}
            carouselId={popularCategories.type}
            isGated={!!popularCategories?.gated}
          />
        </Box>
      ) : null}

      {showInspirationalSection && inspirationalSection ? (
        <Box order={inspirationalSection.position} as="section">
          <RetroRevival items={inspirationalSection.data} />
        </Box>
      ) : null}

      <Box order={LAST_SECTION_ORDER}>
        <UspValueBlock />
      </Box>
      <GeoRedirectCard />
    </>
  );
}

export default HomeView;
